import React, { useState } from 'react'
import { Button } from '@/components/custom/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Plus, PencilLine, Trash2 } from 'lucide-react'
import DataTable from '@/components/custom/data-table'
import PopupConfirm from '@/components/custom/popup-confirm'
import DialogCustomerAttribute from './dialog'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useShallow } from 'zustand/react/shallow'
import useDebounce from '@/hooks/use-debounce'
import { useCustomerAttributeListStore } from '@/store/settings-store/customer/attribute-list-store'
import { getAttributes, deleteAttribute } from '@/api/settings'
import { toast } from 'sonner'
import { throwException } from '@/exceptions/throw-exception'
import { AttributeFieldTypeContext } from '@/constants/setting'
import { useCustomerListFilterStore } from '@/store/customer-list-filter-store'
import QUERY_KEY from '@/constants/use-query-key.js'

export function CustomerAttributes() {
  const queryClient = useQueryClient()
  const [searchText, setSearchText] = useState('')
  const debounceSearchText = useDebounce(searchText, 500)
  const [isOpenDialog, setIsOpenDialog] = useState(false)
  const [editCustomerAttribute, setEditCustomerAttribute] = useState(null)
  const [page, pageSize, setPage, setPageSize] = useCustomerAttributeListStore(
    useShallow((state) => [
      state.page,
      state.pageSize,
      state.setPage,
      state.setPageSize,
    ]),
  )

  const { isLoading, data: queryData } = useQuery({
    queryKey: [
      QUERY_KEY.GET_LIST_USER_ATTRIBUTES,
      { page, pageSize, search: debounceSearchText },
    ],
    queryFn: async () => {
      const params = {
        target: 'user',
        offset: (page - 1) * pageSize,
        limit: pageSize,
      }

      if (searchText) {
        params.title = searchText
      }
      try {
        const res = await getAttributes({ params })
        return res.data || {}
      } catch (error) {
        throwException(error)
        return []
      }
    },
  })

  const data = isLoading ? [] : queryData?.list || []
  const total = isLoading ? 0 : queryData?.total || 0

  const { mutate: handleDeleteCustomerAttribute } = useMutation({
    mutationFn: (customerAttributeId) =>
      deleteAttribute({ id: customerAttributeId }),
    onSuccess: () => {
      toast.success('Xóa thuộc tính khách hàng thành công')
      useCustomerListFilterStore.getState().setRefreshField(new Date())
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY.GET_LIST_USER_ATTRIBUTES],
      })
    },
    onError: throwException,
  })

  const handleSetEditCustomerAttribute = (customerAttribute) => {
    setIsOpenDialog(true)
    setEditCustomerAttribute(customerAttribute)
  }

  const columns = [
    {
      accessorKey: 'title',
      header: () => <div>Tên thuộc tính</div>,
    },
    {
      accessorKey: 'key',
      header: () => <div>Key thuộc tính</div>,
    },
    {
      accessorKey: 'type',
      header: () => <div>Kiểu</div>,
      cell: ({ row }) => {
        const customerAttribute = row.original
        const customerAttributeType = customerAttribute.type

        return <Badge>{AttributeFieldTypeContext[customerAttributeType]}</Badge>
      },
    },
    {
      accessorKey: 'action',
      header: () => <div className="text-center">Hành động</div>,
      className: 'justify-center',
      cell: ({ row }) => {
        const customerAttribute = row.original

        return (
          <div className="flex gap-4">
            <PencilLine
              className="h-4 w-4"
              onClick={(e) => {
                e.stopPropagation()
                handleSetEditCustomerAttribute(customerAttribute)
              }}
            />

            <PopupConfirm
              trigger={
                <Trash2 className="h-4 w-4 cursor-pointer text-[hsl(var(--icon-danger))]" />
              }
              title={'Xóa thuộc tính khách hàng'}
              description={`Bạn có chắc chắn muốn xóa thuộc tính ${customerAttribute.title} không??`}
              onConfirm={() =>
                handleDeleteCustomerAttribute(customerAttribute._id)
              }
            />
          </div>
        )
      },
    },
  ]
  return (
    <React.Fragment>
      <div className="flex h-full w-full flex-col overflow-hidden">
        <Card className="h-full overflow-hidden">
          <CardContent className="flex h-full flex-col gap-2 p-4">
            <div className="sticky top-0 flex flex-col gap-2">
              <h1 className="text-lg font-bold">Thuộc tính khách hàng</h1>
              <div className="grid w-full grid-cols-3">
                <div className="col-span-1">
                  <Input
                    type="text"
                    placeholder="Tìm kiếm theo tên thuộc tính"
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                  />
                </div>
                <div className="col-span-2 flex justify-end">
                  <Button
                    leftSection={<Plus className="h-4 w-4" />}
                    onClick={() => setIsOpenDialog(true)}
                  >
                    Thêm thuộc tính
                  </Button>
                </div>
              </div>
            </div>

            <DataTable
              columns={columns}
              data={data}
              isLoading={isLoading}
              total={total}
              page={page}
              setPage={setPage}
              pageSize={pageSize}
              setPageSize={setPageSize}
            />
          </CardContent>
        </Card>
      </div>

      {isOpenDialog && (
        <DialogCustomerAttribute
          open={isOpenDialog}
          customerAttribute={editCustomerAttribute}
          handleClose={() => {
            setIsOpenDialog(false)
            setEditCustomerAttribute(null)
          }}
        />
      )}
    </React.Fragment>
  )
}
